import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { API_BASE } from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ShieldCheck, Download, Trash2, Lock, Eye, Server, Check, ArrowLeft } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

export default function Rgpd() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);

  const exportData = async () => {
    try {
      const token = localStorage.getItem("prosperr_token");
      const res = await fetch(`${API_BASE}/rgpd/my-data`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "prosperr-mes-donnees.json";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Vos données ont été téléchargées");
    } catch { toast.error("Erreur export"); }
  };

  const deleteAccount = async () => {
    setDeleting(true);
    try {
      await api.delete("/rgpd/delete-account");
      toast.success("Compte supprimé");
      await logout();
      navigate("/");
    } catch { toast.error("Erreur suppression"); }
    finally { setDeleting(false); }
  };

  return (
    <AppShell>
      <div className="px-5 pt-4">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="rounded-full -ml-2" data-testid="back"><ArrowLeft className="w-4 h-4 mr-1" />Retour</Button>
      </div>

      <div className="px-5 mt-2">
        <div className="rounded-3xl p-6 bg-gradient-to-br from-emerald-500 to-teal-500 text-white shadow-lg">
          <ShieldCheck className="w-10 h-10 opacity-90" />
          <h1 className="font-display text-2xl sm:text-3xl font-bold mt-3" data-testid="rgpd-title">Vos données, votre contrôle</h1>
          <p className="text-sm text-white/90 mt-2 leading-relaxed">
            Prosperr est <strong>100% RGPD</strong>. Nous ne vendons, ne revendons et n'exploitons <strong>jamais</strong> vos données. Hébergement européen, chiffrement de bout en bout, suppression à la demande.
          </p>
        </div>
      </div>

      {/* Principles */}
      <div className="px-5 mt-5 grid gap-3">
        {[
          { icon: Lock, title: "Aucune collecte non essentielle", desc: "Nous collectons uniquement ce qui est strictement nécessaire au fonctionnement de votre compte. Pas de trackers publicitaires, pas de cookies tiers." },
          { icon: Eye, title: "Vos leads vous appartiennent", desc: "Vos données de prospection ne sont accessibles qu'à vous (et à votre équipe si plan Entreprise). Elles ne servent jamais à entraîner d'autres modèles." },
          { icon: Server, title: "Hébergement européen", desc: "Nos serveurs sont hébergés en France et en Europe. Aucun transfert hors UE sans votre consentement explicite." },
          { icon: ShieldCheck, title: "Chiffrement TLS + stockage sécurisé", desc: "Toutes les communications sont chiffrées (HTTPS). Les mots de passe sont hachés (bcrypt) et les paiements passent par Stripe (PCI-DSS)." },
          { icon: Check, title: "Portabilité garantie", desc: "Export complet en JSON en un clic. Import et export Excel/CSV disponibles sur toutes les vues." },
        ].map((p, i) => (
          <div key={i} className="bg-[var(--surface)] rounded-xl p-4 border border-[var(--border-soft)] shadow-sm flex items-start gap-3" data-testid={`principle-${i}`}>
            <div className="w-10 h-10 rounded-xl bg-emerald-500/10 text-emerald-300 grid place-items-center shrink-0"><p.icon className="w-5 h-5" /></div>
            <div>
              <div className="font-display font-bold text-[var(--text)]">{p.title}</div>
              <p className="text-sm text-zinc-400 mt-0.5 leading-relaxed">{p.desc}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Actions */}
      <div className="px-5 mt-6">
        <h2 className="font-display font-bold text-[var(--text)] mb-2">Exercez vos droits</h2>
        <div className="space-y-2">
          <Button onClick={exportData} variant="outline" className="w-full rounded-2xl h-12 justify-start" data-testid="export-data">
            <Download className="w-4 h-4 mr-2" />Télécharger toutes mes données (JSON)
          </Button>
          <Button onClick={() => setConfirmOpen(true)} variant="outline" className="w-full rounded-2xl h-12 justify-start text-rose-300 border-rose-500/40 hover:bg-rose-500/100/10 hover:text-rose-300" data-testid="delete-account">
            <Trash2 className="w-4 h-4 mr-2" />Supprimer mon compte et toutes mes données
          </Button>
        </div>
      </div>

      <div className="px-5 mt-5 text-xs text-zinc-500 leading-relaxed space-y-2">
        <p><strong>Délégué à la Protection des Données (DPO)</strong> : dpo@prosperr.fr</p>
        <p>Vous disposez des droits d'accès, de rectification, d'effacement, d'opposition, de limitation et de portabilité (art. 15 à 22 RGPD). Pour les exercer, utilisez les boutons ci-dessus ou contactez notre DPO. Vous pouvez également saisir la CNIL.</p>
      </div>

      {/* Delete confirmation */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle className="text-rose-300">Supprimer définitivement mon compte</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-zinc-300">Cette action est <strong>irréversible</strong>. Toutes vos données (prospects, interactions, campagnes, équipe) seront effacées définitivement.</p>
            <p className="text-xs text-zinc-500">Pour confirmer, tapez <strong>SUPPRIMER</strong> ci-dessous :</p>
            <input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} className="w-full rounded-xl border border-[var(--border)] p-2.5 text-sm" data-testid="delete-confirm-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} className="rounded-full">Annuler</Button>
            <Button onClick={deleteAccount} disabled={confirmText !== "SUPPRIMER" || deleting} className="rounded-full bg-red-600 hover:bg-red-700" data-testid="confirm-delete">
              {deleting ? "Suppression..." : "Supprimer pour toujours"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
